import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { DateTime } from 'luxon'
import { formatDateForDatabase } from '@utils'
import { LoadingButton, TabContext, TabPanel } from '@mui/lab'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { Fragment, useEffect, useState } from 'react'
import CheckOrgOrdersModal from '@components/CheckOrgOrdersModal'
import DashboardLayout from '../components/DashboardLayout'
import Flag from 'react-world-flags'
import MultiSelectAutocomplete, {
    OptionType,
} from '@components/MultiSelectAutocomplete'
import PeriodDatePicker from '@components/PeriodDatePicker'
import ReceiptLong from '@mui/icons-material/ReceiptLong'
import ValidationErrors from '@components/ValidationErrors'
import API from '@enums/api'

const DEFAULT_FROM_DATE = DateTime.now().startOf('month')
const DEFAULT_TO_DATE = DateTime.now().endOf('month')

const SendInvoicesPage = () => {
    const [hasAccessToken, setHasAccessToken] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const checkAccessToken = async () => {
            const res = await API.ha.accounting.hasXeroToken()

            if (!res.ok) {
                console.error('Failed to check access token')
                return
            }

            setHasAccessToken(res.value.hasToken)
        }
        checkAccessToken()
    }, [])

    return (
        <DashboardLayout>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                {hasAccessToken && <SendInvoices />}
                {!hasAccessToken && <ConnectXeroComponent />}
            </Box>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <LoadingButton
                    startIcon={<ReceiptLong />}
                    size="small"
                    variant="contained"
                    onClick={() => setOpen(true)}
                    style={{ marginTop: 32 }}
                >
                    Open Order Checker
                </LoadingButton>
            </Box>
            <CheckOrgOrdersModal open={open} setOpen={setOpen} />
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}

const OrderInvoices = (props: {
    loading: boolean
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    setSuccess: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    const { loading, setError, setLoading, setSuccess } = props

    const [country, setCountry] = useState<string>('USA')
    const [options, setOptions] = useState<OptionType[]>([])
    const [selectedOrgs, setSelectedOrgs] = useState<OptionType[]>([])

    const [fromDate, setFromDate] = useState<DateTime | null | undefined>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | null | undefined>(
        DEFAULT_TO_DATE
    )

    const [createInvoiceResponse, setCreateInvoiceResponse] = useState<null | {
        created: {
            allNames: string[]
        }
        failed: {
            allFailed: string[]
        }
    }>(null)

    const handleCountrySelect = (event: SelectChangeEvent<string>) => {
        setCountry(event.target.value as string)
    }

    // Fetch the list of organisations that are have
    //  the country code US or USA or United States
    //  and set the options to the response
    useEffect(() => {
        const fetchOrgs = async () => {
            const res = await API.searches.getOrgsByCountry(country)

            if (!res.ok) {
                setError('Failed to fetch orgs')
                return
            }

            const data = res.value
            setOptions(data)
        }
        fetchOrgs()
        setSelectedOrgs([])
    }, [country])

    const handleSubmit = async () => {
        setLoading(true)

        setCreateInvoiceResponse(null)

        if (selectedOrgs.length === 0) {
            setError('Please select at least one organisation')
            setLoading(false)
            return
        }

        const fromDateFmt = formatDateForDatabase(fromDate, 'start')
        const toDateFmt = formatDateForDatabase(toDate, 'end')

        if (!fromDateFmt || !toDateFmt) {
            setError(
                `Something went wrong setting date range: ${fromDateFmt} - ${toDateFmt}`
            )
            setLoading(false)
            return
        }

        // call the endpoint to add send the invoice
        const res = await API.ha.accounting.sendOrderInvoices({
            orgId: selectedOrgs.map((org) => org.id) as string[], // TODO: change to orgIds
            from: fromDateFmt,
            to: toDateFmt,
        })

        if (!res.ok) {
            setError(`Failed to send invoices, the reason was: ${res.message}`)
            setLoading(false)
            return
        }

        setCreateInvoiceResponse(res.value)

        setLoading(false)
        setSuccess(true)
    }
    return (
        <Fragment>
            <Typography style={{ marginBottom: 16 }}>
                Select the Country:
            </Typography>
            <FormControl fullWidth style={{ marginBottom: 16 }}>
                <InputLabel id="countrySelector">Country</InputLabel>
                <Select
                    labelId="countrySelector"
                    label="Country"
                    value={country}
                    onChange={handleCountrySelect}
                    className="countrySelector"
                >
                    <MenuItem value={'USA'}>
                        <Flag code="US" style={{ width: '10%' }} /> United
                        States
                    </MenuItem>
                    <MenuItem value={'CAN'}>
                        <Flag code="CA" style={{ width: '10%' }} /> Canada
                    </MenuItem>
                </Select>
            </FormControl>
            <Typography style={{ marginBottom: 16 }}>
                Select the org(s) you want to invoice for:
            </Typography>
            <MultiSelectAutocomplete
                options={options}
                label="Organisations"
                placeholder="Select organisations"
                selectedOptions={selectedOrgs}
                onSelectionChange={setSelectedOrgs}
            />
            <Typography style={{ marginTop: 32 }}>
                Select the month and Year you want to invoice for:
            </Typography>
            <PeriodDatePicker
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
            />
            <Typography variant="body1" style={{ marginTop: 16 }}>
                {formatDateForDatabase(fromDate, 'start')?.replace('T', ' ')} -{' '}
                {formatDateForDatabase(toDate, 'end')}
            </Typography>
            <LoadingButton
                disabled={loading || selectedOrgs.length === 0}
                startIcon={<ReceiptLong />}
                size="small"
                variant="contained"
                onClick={handleSubmit}
                style={{ marginTop: 32 }}
            >
                Send Invoices
            </LoadingButton>

            {createInvoiceResponse && (
                <Box
                    style={{
                        marginTop: '2rem',
                    }}
                >
                    <Stack
                        justifyContent={'flex-start'}
                        gap={1}
                        alignItems={'flex-start'}
                    >
                        <Box>
                            <b>Created: </b>
                        </Box>
                        {createInvoiceResponse.created.allNames.map((name) => {
                            return <Box> {name}</Box>
                        })}
                    </Stack>

                    <ValidationErrors
                        messages={createInvoiceResponse.failed.allFailed}
                    />
                </Box>
            )}
        </Fragment>
    )
}

const LicenseInvoices = (props: {
    loading: boolean
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    setSuccess: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    const [country, setCountry] = useState<string>('USA')
    const [options, setOptions] = useState<OptionType[]>([])
    const [selectedOrgs, setSelectedOrgs] = useState<OptionType[]>([])

    const [fromDate, setFromDate] = useState<DateTime | null | undefined>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | null | undefined>(
        DEFAULT_TO_DATE
    )
    const { loading, setLoading, setError, setSuccess } = props

    const [allOrgs, setAllOrgs] = useState<boolean>(true)

    const [createInvoiceResponse, setCreateInvoiceResponse] = useState<null | {
        created: {
            allNames: string[]
            allIds: string[]
        }
        failed: {
            byId: Record<string, string>
            allFailed: string[]
        }
    }>(null)
    async function sendInvoices() {
        setLoading(true)

        const fromDateFmt = formatDateForDatabase(fromDate, 'start')
        const toDateFmt = formatDateForDatabase(toDate, 'end')

        if (!fromDateFmt || !toDateFmt) {
            setError(
                `Something went wrong setting date range: ${fromDateFmt} - ${toDateFmt}`
            )
            setLoading(false)
            return
        }

        const res = await API.ha.accounting.sendLicenseInvoices({
            orgId: allOrgs
                ? undefined
                : (selectedOrgs.map((o) => o.id) as string[]),
            allOrgs,
            from: fromDateFmt,
            to: toDateFmt,
        })

        if (!res.ok) {
            setError(`Failed to send invoices, the reason was: ${res.message}`)
            setLoading(false)
            return
        }

        setCreateInvoiceResponse(res.value)
        setLoading(false)
        setSuccess(true)
    }

    const handleCountrySelect = (event: SelectChangeEvent<string>) => {
        setCountry(event.target.value as string)
    }

    useEffect(() => {
        const fetchOrgs = async () => {
            const res = await API.searches.getOrgsByCountry(country)

            if (!res.ok) {
                setError('Failed to fetch orgs')
                return
            }

            setOptions(res.value)
        }
        fetchOrgs()
        setSelectedOrgs([])
    }, [country])

    return (
        <Fragment>
            <Typography variant="body1" marginBottom={4}>
                Generate for all organizations or for selected orgs:
            </Typography>
            <Stack
                direction={'row'}
                width={'95%'}
                columnGap={2}
                justifyContent={'space-between'}
                marginBottom={4}
                sx={{ verticalAlign: 'middle' }}
            >
                <FormControl sx={{ width: '50%', my: 'auto' }}>
                    <ToggleButtonGroup
                        exclusive
                        value={allOrgs}
                        onChange={(_, v) => setAllOrgs(v)}
                    >
                        <ToggleButton value={true} fullWidth>
                            All
                        </ToggleButton>
                        <ToggleButton value={false} fullWidth>
                            Some
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
                <Stack direction={'column'} width={'75%'}>
                    <Typography style={{ marginBottom: 16 }}>
                        Select the Country:
                    </Typography>
                    <FormControl fullWidth style={{ marginBottom: 16 }}>
                        <InputLabel id="countrySelector">Country</InputLabel>
                        <Select
                            labelId="countrySelector"
                            label="Country"
                            value={country}
                            onChange={handleCountrySelect}
                            className="countrySelector"
                            disabled={allOrgs}
                        >
                            <MenuItem value={'USA'}>
                                <Flag code="US" style={{ width: '10%' }} />{' '}
                                United States
                            </MenuItem>
                            <MenuItem value={'CAN'}>
                                <Flag code="CA" style={{ width: '10%' }} />{' '}
                                Canada
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Typography style={{ marginBottom: 16 }}>
                        Select the org(s) you want to invoice for:
                    </Typography>
                    <MultiSelectAutocomplete
                        options={options}
                        label="Organisations"
                        placeholder="Select organisations"
                        selectedOptions={selectedOrgs}
                        onSelectionChange={setSelectedOrgs}
                        disabled={allOrgs}
                        style={{ width: '100%' }}
                    />
                </Stack>
            </Stack>

            <Typography variant="body1">
                Select the month and year to invoice for:
            </Typography>
            <PeriodDatePicker
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                monthOnly
            />

            <LoadingButton
                disabled={loading || (!allOrgs && selectedOrgs.length === 0)}
                startIcon={<ReceiptLong />}
                size="small"
                variant="contained"
                onClick={sendInvoices}
                style={{ marginTop: 32 }}
            >
                Send Invoices
            </LoadingButton>

            {createInvoiceResponse && (
                <Box
                    style={{
                        marginTop: '2rem',
                    }}
                >
                    <Stack
                        justifyContent={'flex-start'}
                        gap={1}
                        alignItems={'flex-start'}
                    >
                        <Box>
                            <b>Created: </b>
                        </Box>
                        {createInvoiceResponse.created.allNames.map((name) => {
                            return <Box> {name}</Box>
                        })}
                    </Stack>

                    <ValidationErrors
                        messages={createInvoiceResponse.failed.allFailed}
                    />
                </Box>
            )}
        </Fragment>
    )
}

const SendInvoices = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)

    const [tab, setTab] = useState('0')

    if (error) {
        enqueueSnackbar(`Failed to send invoices`, { variant: 'error' })
    }
    if (success) {
        enqueueSnackbar(`Invoices sent}`, { variant: 'success' })
        setSuccess(false)
    }

    function a11yProps(index: number) {
        return {
            id: `tab-${index}`,
            'aria-controls': `tabpanel-${index}`,
        }
    }

    return (
        <Paper
            style={{
                paddingInline: 32,
                paddingBlock: 32,
                textAlign: 'center',
                minWidth: '40em',
            }}
        >
            <Typography variant="h5" component="h2" gutterBottom>
                Send Invoices
            </Typography>
            <Box sx={{ width: '100%' }}>
                <TabContext value={tab}>
                    <Tabs
                        value={tab}
                        onChange={(_, newValue) => setTab(newValue)}
                    >
                        <Tab label="Orders" value={'0'} {...a11yProps(0)} />
                        <Tab label="Licenses" value={'1'} {...a11yProps(1)} />
                    </Tabs>

                    <TabPanel value={'0'}>
                        <OrderInvoices
                            loading={loading}
                            setLoading={setLoading}
                            setError={setError}
                            setSuccess={setSuccess}
                        />
                    </TabPanel>
                    <TabPanel value={'1'}>
                        <LicenseInvoices
                            loading={loading}
                            setLoading={setLoading}
                            setError={setError}
                            setSuccess={setSuccess}
                        />
                    </TabPanel>
                </TabContext>
            </Box>
        </Paper>
    )
}

const ConnectXeroComponent = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)

    const handleSubmit = async () => {
        setLoading(true)

        const res = await API.ha.accounting.xeroAuth()
        if (!res.ok) {
            setError(
                `Failed to connect to Xero, the reason was: ${res.message}`
            )
            setLoading(false)
            return
        }

        const data = res.value
        console.log(data.consentUrl)
        window.open(data.consentUrl, '_blank')
        setSuccess(true)
    }

    if (loading) {
        enqueueSnackbar('Loading...', { variant: 'info' })
    }
    if (error) enqueueSnackbar(error, { variant: 'error' })
    if (success) {
        enqueueSnackbar(`Successfully Connected to XERO`, {
            variant: 'success',
        })
        setSuccess(false)
    }

    return (
        <Paper
            style={{
                paddingInline: 32,
                paddingBlock: 32,
                textAlign: 'center',
            }}
        >
            <Typography>
                Connect your account to Xero to send invoices
            </Typography>
            <LoadingButton
                startIcon={<ReceiptLong />}
                size="small"
                variant="contained"
                style={{ marginTop: 32 }}
                onClick={handleSubmit}
                disabled={loading}
            >
                Connect to Xero
            </LoadingButton>
        </Paper>
    )
}

export default SendInvoicesPage
